import { supabase } from '../lib/supabase';
import { ENV } from '../config/env';
import { 
  BusinessConsultantChat, 
  BusinessConsultantMessage,
  ConsultantType,
  CreateChatParams,
  CreateMessageParams,
  BusinessConsultantService as IBusinessConsultantService
} from '../types/businessConsultant';
import { 
  handleHttpError, 
  handleServiceError, 
  createAppError, 
  ErrorCategory,
  withErrorHandling 
} from '../utils/errorHandler';

/**
 * Implementation of the Business Consultant Service
 * Handles interactions with Supabase and the Anthropic API for the virtual business consultant
 */
export class BusinessConsultantService implements IBusinessConsultantService {
  
  // Get the user ID from Supabase - standardized across services
  private async getUserId(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      const userId = data.session?.user?.id || '';
      if (!userId) {
        console.error('Error: Missing user ID in session');
      }
      return userId;
    } catch (e) {
      console.error('Error getting Supabase user ID:', e);
      return '';
    }
  }
  
  // Validate authentication before proceeding with operations
  private async validateAuthentication(): Promise<string> {
    const userId = await this.getUserId();
    if (!userId) {
      throw createAppError('User is not authenticated', ErrorCategory.AUTHENTICATION);
    }
    return userId;
  }
  
  // Get authorization token for API calls
  private async getAuthToken(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token || '';
    } catch (e) {
      console.error('Error getting auth token:', e);
      return '';
    }
  }
  
  /**
   * Get the user's consent status for the Business Consultant module
   */
  async getConsentStatus(): Promise<boolean> {
    return withErrorHandling(async () => {
      try {
        // Get user ID first to ensure authentication
        const userId = await this.getUserId();
        if (!userId) {
          console.warn('No user ID available when checking consent status');
          return false;
        }
        
        const { data, error } = await supabase
          .from('business_consultant_consents')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking consent status:', error);
          return false;
        }

        return data?.has_accepted || false;
      } catch (err) {
        console.error('Error in getConsentStatus:', err);
        return false;
      }
    }, { service: 'BusinessConsultantService', method: 'getConsentStatus' }, false);
  }

  /**
   * Record the user's consent status for the Business Consultant module
   */
  async recordConsent(hasConsented: boolean): Promise<void> {
    return withErrorHandling(async () => {
      try {
        // Use the standard method to validate authentication
        const userId = await this.validateAuthentication();

        const { error } = await supabase
          .from('business_consultant_consents')
          .upsert({
            user_id: userId,
            has_accepted: hasConsented,
            accepted_at: new Date().toISOString()
          });

          if (error) {
          console.error('Error recording consent:', error);
          throw createAppError('Failed to record consent status', ErrorCategory.STORAGE, undefined, undefined, { error });
        }
      } catch (err: any) {
        console.error('Error in recordConsent:', err);
        // Re-throw error if it's already an AppError, otherwise wrap it
        if (err?.name === 'AppError') {
          throw err;
        }
        throw createAppError('Failed to record consent status', ErrorCategory.UNKNOWN, undefined, undefined, { originalError: err });
      }
    }, { service: 'BusinessConsultantService', method: 'recordConsent' });
  }

  /**
   * Get all chats for the current user
   */
  async getChats(): Promise<BusinessConsultantChat[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID using standardized method and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching business consultant chats for user:', { userId });
        
        const { data, error } = await supabase
          .from('business_consultant_chats')
          .select('*')
          .eq('user_id', userId) // Ensure we only fetch chats for the current user
          .eq('is_archived', false)
          .order('updated_at', { ascending: false });

        if (error) {
          console.error('Error fetching chats:', error);
          throw createAppError(
            'Failed to fetch chats', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your conversations',
            { error }
          );
        }

        return data || [];
      } catch (err: any) {
        console.error('Error in getChats:', err);
        
        // Re-throw AppErrors, wrap others
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch chats', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an error loading your conversations',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'getChats' });
  }

  /**
   * Get a specific chat by ID
   */
  async getChatById(chatId: string): Promise<BusinessConsultantChat | null> {
    return withErrorHandling(async () => {
      try {
        // Get user ID using standardized method and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching business consultant chat by ID:', { chatId, userId });
        
        const { data, error } = await supabase
          .from('business_consultant_chats')
          .select('*')
          .eq('id', chatId)
          .eq('user_id', userId) // Security: ensure user can only access their own chats
          .single();

        if (error) {
          console.error('Error fetching chat:', error);
          throw createAppError(
            'Failed to fetch chat', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading the conversation',
            { error }
          );
        }

        return data;
      } catch (err: any) {
        console.error('Error in getChatById:', err);
        
        // Re-throw AppErrors, wrap others
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch chat', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an error loading the conversation',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'getChatById' });
  }

  /**
   * Create a new chat with the specified consultant type
   */
  async createChat(params: CreateChatParams): Promise<BusinessConsultantChat> {
    return withErrorHandling(async () => {
      try {
        // Get user ID using our standardized method
        const userId = await this.validateAuthentication();
  
        console.log('Creating new business consultant chat:', {
          consultantType: params.consultant_type,
          chatName: params.chat_name,
          userId
        });
  
        const newChat = {
          user_id: userId,
          chat_name: params.chat_name,
          consultant_type: params.consultant_type,
          idea_id: params.idea_id || null,
          business_plan_id: params.business_plan_id || null,
          tokens_used: 0,
          is_archived: false
        };
        
        const { data, error } = await supabase
          .from('business_consultant_chats')
          .insert(newChat)
          .select('*')
          .single();
  
        if (error) {
          console.error('Error creating chat:', error);
          throw createAppError(
            `Failed to create chat: ${error.message}`,
            ErrorCategory.STORAGE,
            undefined,
            'Unable to create a new conversation. Please try again.',
            { error }
          );
        }
  
        if (!data) {
          throw createAppError(
            'Failed to create chat: No data returned',
            ErrorCategory.STORAGE,
            undefined,
            'Unable to create a new conversation. Please try again.'
          );
        }
        
        console.log('Successfully created chat:', { chatId: data.id });
        
        // Create welcome message for new chat
        await this.createMessage({
          chat_id: data.id,
          role: 'assistant',
          content: this.getWelcomeMessage(params.consultant_type)
        });
  
        // Return data with correct property mapping
        return {
          ...data,
          total_tokens_used: data.tokens_used // Map database column to interface property
        };
      } catch (err: any) {
        console.error('Error in createChat:', err);
        
        // Re-throw AppErrors, wrap others
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          `Failed to create chat: ${err?.message || 'Unknown error'}`,
          ErrorCategory.UNKNOWN,
          undefined,
          'Unable to create a new conversation. Please try again.',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'createChat' });
  }

  /**
   * Archive an existing chat (soft delete)
   */
  async archiveChat(chatId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('business_consultant_chats')
        .update({ is_archived: true })
        .eq('id', chatId);

      if (error) {
        console.error('Error archiving chat:', error);
        throw new Error('Failed to archive chat');
      }
    } catch (err) {
      console.error('Error in archiveChat:', err);
      throw new Error('Failed to archive chat');
    }
  }

  /**
   * Get all messages for a specific chat
   */
  async getMessagesByChatId(chatId: string): Promise<BusinessConsultantMessage[]> {
    try {
      const { data, error } = await supabase
        .from('business_consultant_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        throw new Error('Failed to fetch messages');
      }

      return data || [];
    } catch (err) {
      console.error('Error in getMessagesByChatId:', err);
      throw new Error('Failed to fetch messages');
    }
  }

  /**
   * Create a new message in a chat
   */
  async createMessage(params: CreateMessageParams): Promise<BusinessConsultantMessage> {
    try {
      const newMessage = {
        ...params,
        tokens_used: 0
      };
      
      const { data, error } = await supabase
        .from('business_consultant_messages')
        .insert(newMessage)
        .select('*')
        .single();

      if (error) {
        console.error('Error creating message:', error);
        throw new Error('Failed to create message');
      }

      if (!data) {
        throw new Error('Failed to create message: No data returned');
      }

      // Update chat timestamp
      await supabase
        .from('business_consultant_chats')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', params.chat_id);

      return data;
    } catch (err) {
      console.error('Error in createMessage:', err);
      throw new Error('Failed to create message');
    }
  }
  
  /**
   * Send a user message to a chat and get an AI response
   */
  async sendMessageToChatAndGetResponse(
    chatId: string,
    content: string,
    includeBusiness: boolean = false
  ): Promise<BusinessConsultantMessage> {
    return withErrorHandling(async () => {
      try {
        // First validate auth using our standardized method
        const userId = await this.validateAuthentication();
        const authToken = await this.getAuthToken();
        
        if (!authToken) {
          throw createAppError('Missing authentication token', ErrorCategory.AUTHENTICATION);
        }
        
        // Get chat details to know which consultant type
        const chat = await this.getChatById(chatId);
        if (!chat) {
          throw createAppError('Chat not found', ErrorCategory.VALIDATION, undefined, 'Chat not found. Please try creating a new conversation.');
        }

        // Log request for debugging
        console.log('Sending message to API:', {
          chatId,
          consultantType: chat.consultant_type,
          includeBusiness,
          timestamp: new Date().toISOString()
        });
        
        // Add user message to chat
        await this.createMessage({
          chat_id: chatId,
          role: 'user',
          content: content
        });
      
        // Prepare API request with consistent structure
        const response = await fetch(`${ENV.API_URL}/api/business-consultant/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            userId: userId,
            chat_id: chatId,
            message: content,
            include_business_data: includeBusiness,
            consultant_type: chat.consultant_type
          }),
        });

        if (!response.ok) {
          const status = response.status;
          let errorMessage = `API error ${status}`;
          
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
            console.error('API response error:', { status, errorData });
          } catch (e) {
            const errorText = await response.text();
            console.error('API response error (raw):', { status, errorText });
          }
          
          throw createAppError(
            `Failed to get AI response: ${errorMessage}`, 
            ErrorCategory.API, 
            status,
            'Failed to get response from the AI assistant. Please try again later.'
          );
        }

        const responseData = await response.json();
        console.log('Received API response:', { success: true, hasMessage: !!responseData.message });
        
        // Create AI message in database
        const aiMessage = await this.createMessage({
          chat_id: chatId,
          role: 'assistant',
          content: responseData.message
        });

        // Update token counts
        if (chat.total_tokens_used !== undefined && responseData.tokens_used) {
          await supabase
            .from('business_consultant_chats')
            .update({
              total_tokens_used: chat.total_tokens_used + responseData.tokens_used
            })
            .eq('id', chatId);
        }

        // Update the message with token count
        if (responseData.tokens_used) {
          await supabase
            .from('business_consultant_messages')
            .update({
              tokens_used: responseData.tokens_used
            })
            .eq('id', aiMessage.id);
        }

        return aiMessage;
      } catch (err: any) {
        console.error('Error sending message:', err);
        
        // Re-throw AppErrors, wrap other errors
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to process message', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an error sending your message. Please try again.',
          { originalError: err }
        );
      }
    }, { service: 'BusinessConsultantService', method: 'sendMessageToChatAndGetResponse' });
  }
  
  /**
   * Helper method to get welcome message based on consultant type
   */
  private getWelcomeMessage(consultantType: ConsultantType): string {
    const disclaimerNote = `**Important:** My advice is AI-generated and should not replace professional consultation. Please verify any information before making business decisions.`;
    
    switch (consultantType) {
      case 'marketing':
        return `👋 Hello! I'm Keith, your Marketing Strategist. I specialize in digital marketing, brand positioning, and customer acquisition strategies. What marketing challenge can I assist you with today?\n\n${disclaimerNote}`;
      
      case 'finance':
        return `👋 Hello! I'm Carol, your Financial Advisor. I can help with financial planning, funding options, and optimizing your business financials. What financial question can I address for you today?\n\n${disclaimerNote}`;
      
      case 'strategy':
        return `👋 Hello! I'm Nikita, your Business Strategist. I specialize in business planning, competitive analysis, and growth strategies. What strategic challenge are you facing today?\n\n${disclaimerNote}`;
      
      default:
        return `👋 Hello! I'm your Virtual Business Consultant. How can I help you today?\n\n${disclaimerNote}`;
    }
  }
}

/**
 * Singleton instance of the Business Consultant Service
 */
export const businessConsultantService = new BusinessConsultantService();
